import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { downloadAnalyticalDatasetExport } from '../api/downloadAnalyticalDatasetExport';
import type { UseAdDownloadModalArgs, UseAdDownloadModalResult } from '../types';
import { useAdRouteParams } from './useAdRouteParams';

export const useAdDownloadModal = ({
  onClose,
}: UseAdDownloadModalArgs): UseAdDownloadModalResult => {
  const { formatMessage } = useIntl();
  const { projectId, analyticalDatasetProfileId } = useAdRouteParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingError, setSubmittingError] = useState<Error | null>(null);
  // Bumping this re-runs the download effect — wired to the error-state Retry.
  const [attempt, setAttempt] = useState(0);

  // Keep `onClose` current without making it a download-effect dependency, so a
  // parent re-render can't re-fire the one-shot download.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Fire the download from the effect rather than a react-query mutation. Under
  // StrictMode's mount -> unmount -> mount, a mutation observer detaches from its
  // in-flight mutation on the throwaway unmount and never re-attaches
  // (MutationObserver.onUnsubscribe removes the observer; onSubscribe is a no-op) —
  // so the file downloads but neither the observer's `isSuccess` state nor its
  // `onSuccess` callback ever fires, leaving the modal stuck open. The api layer
  // shares one in-flight request per profile, so the remount joins the same fetch.
  //
  // The fetch is intentionally NEVER aborted: closing the modal only stops this
  // hook from touching state (`disposed`) — the download keeps running in the
  // background and the file still saves when it resolves.
  useEffect(() => {
    if (!projectId || !analyticalDatasetProfileId) return;

    let disposed = false;
    setIsSubmitting(true);
    setSubmittingError(null);

    downloadAnalyticalDatasetExport({ projectId, analyticalDatasetProfileId })
      .then(() => {
        if (!disposed) onCloseRef.current();
      })
      .catch((err: unknown) => {
        if (!disposed)
          setSubmittingError(err instanceof Error ? err : new Error('Download failed'));
      })
      .finally(() => {
        if (!disposed) setIsSubmitting(false);
      });

    return () => {
      disposed = true;
    };
  }, [projectId, analyticalDatasetProfileId, attempt]);

  const onRetry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    title: formatMessage({ id: 'analyticalDatasetDetail.downloadModal.title' }),
    loadingTitle: formatMessage({ id: 'analyticalDatasetDetail.downloadModal.loadingTitle' }),
    loadingText: formatMessage({ id: 'analyticalDatasetDetail.downloadModal.loadingText' }),
    errorTitle: formatMessage({ id: 'analyticalDatasetDetail.downloadModal.errorTitle' }),
    errorText: formatMessage({ id: 'analyticalDatasetDetail.downloadModal.errorText' }),
    retryButtonLabel: formatMessage({ id: 'analyticalDatasetDetail.downloadModal.retryButton' }),
    isSubmitting,
    submittingError,
    onRetry,
  };
};
